import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Gift, ShoppingCart, PlayCircle, RefreshCw, Clock, Users, Mail, Loader2, Star, Package, Heart, UserX, BarChart3, Copy } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminCampaignsPanel = () => {
  const { token } = useAuth();
  const { language } = useLanguage();
  const [status, setStatus] = useState(null);
  const [emailStats, setEmailStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [runningJob, setRunningJob] = useState(null);
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const reload = async () => {
    setLoading(true);
    try {
      const [statusRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/campaigns/status`, authHeaders),
        axios.get(`${API}/admin/campaigns/email-stats`, authHeaders).catch(() => ({ data: null })),
      ]);
      setStatus(statusRes.data);
      setEmailStats(statsRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    Promise.resolve().then(() => reload());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runCampaign = async (job) => {
    setRunningJob(job);
    try {
      const urlMap = {
        birthday: `${API}/admin/campaigns/birthday/run`,
        'cart-abandonment': `${API}/admin/campaigns/cart-abandonment/run`,
        'post-delivery-review': `${API}/admin/campaigns/post-delivery-review/run`,
        reactivation: `${API}/admin/campaigns/reactivation/run`,
      };
      const { data } = await axios.post(urlMap[job], {}, authHeaders);
      const s = data.stats || {};
      toast.success(
        language === 'fr'
          ? `Envoyés: ${s.sent || 0} · Échecs: ${s.failed || 0} · Ignorés: ${s.skipped || 0}`
          : `Sent: ${s.sent || 0} · Failed: ${s.failed || 0} · Skipped: ${s.skipped || 0}`,
      );
      await reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Campaign run failed');
    } finally {
      setRunningJob(null);
    }
  };

  const formatNext = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleString(language === 'fr' ? 'fr-FR' : 'en-GB', {
        dateStyle: 'medium', timeStyle: 'short',
      });
    } catch {
      return iso;
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const s = status?.stats || {};

  return (
    <div className="space-y-6" data-testid="admin-campaigns-panel">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Gift className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Anniv. envoyés' : 'Birthdays sent'} ({s.current_year})</p>
                <p className="text-xl font-bold" data-testid="stat-birthdays-sent">{s.birthdays_sent_this_year ?? 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <ShoppingCart className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Rappels panier' : 'Cart reminders'}</p>
                <p className="text-xl font-bold" data-testid="stat-cart-reminders-sent">{s.cart_reminders_sent_total ?? 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                <Star className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{language === 'fr' ? "Demandes d'avis" : 'Review requests'}</p>
                <p className="text-xl font-bold" data-testid="stat-review-reminders-sent">{s.review_reminders_sent_total ?? 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
                <Heart className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Réactivations' : 'Reactivations'}</p>
                <p className="text-xl font-bold" data-testid="stat-reactivation-sent">{s.reactivation_emails_sent_total ?? 0}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">{s.dormant_customers_count ?? 0} {language === 'fr' ? 'dormants' : 'dormant'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">{language === 'fr' ? 'DDN renseignées' : 'DoB provided'}</p>
                <p className="text-xl font-bold" data-testid="stat-users-with-dob">{s.users_with_date_of_birth ?? 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Scheduler status */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#0F4C5C]" />
              {language === 'fr' ? 'Campagnes email programmées' : 'Scheduled email campaigns'}
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              {language === 'fr'
                ? 'Les campagnes s\'exécutent automatiquement. Cliquez sur "Lancer maintenant" pour un envoi immédiat.'
                : 'Campaigns run automatically. Click "Run now" for an immediate send.'}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={reload} data-testid="campaigns-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-1" /> {language === 'fr' ? 'Actualiser' : 'Refresh'}
          </Button>
        </CardHeader>
        <CardContent>
          {!status?.scheduler_running && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-900">
              {language === 'fr' ? '⚠️ Scheduler arrêté' : '⚠️ Scheduler not running'}
            </div>
          )}
          <div className="space-y-3">
            {/* Birthday */}
            <div className="flex items-center justify-between p-4 border border-border rounded-lg" data-testid="campaign-row-birthday">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                  <Gift className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="font-semibold flex items-center gap-2">
                    {language === 'fr' ? 'Anniversaires' : 'Birthdays'}
                    <Badge variant="outline" className="text-xs">-20% · 30j</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                    <Clock className="w-3 h-3" />
                    {language === 'fr' ? 'Prochain envoi' : 'Next run'} :{' '}
                    {formatNext(status?.jobs?.find((j) => j.id === 'birthday_campaign')?.next_run_time)}
                    <span className="mx-1">·</span>
                    {language === 'fr' ? 'Quotidien 09:00 UTC' : 'Daily 09:00 UTC'}
                  </p>
                </div>
              </div>
              <Button
                onClick={() => runCampaign('birthday')}
                disabled={runningJob === 'birthday'}
                size="sm"
                data-testid="run-birthday-campaign-btn"
              >
                {runningJob === 'birthday' ? (
                  <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> {language === 'fr' ? 'En cours…' : 'Running…'}</>
                ) : (
                  <><PlayCircle className="w-4 h-4 mr-1" /> {language === 'fr' ? 'Lancer maintenant' : 'Run now'}</>
                )}
              </Button>
            </div>

            {/* Cart abandonment */}
            <div className="flex items-center justify-between p-4 border border-border rounded-lg" data-testid="campaign-row-cart">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-400 to-indigo-600 flex items-center justify-center">
                  <ShoppingCart className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="font-semibold flex items-center gap-2">
                    {language === 'fr' ? 'Panier abandonné' : 'Abandoned cart'}
                    <Badge variant="outline" className="text-xs">-10% · 48h</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                    <Clock className="w-3 h-3" />
                    {language === 'fr' ? 'Prochain envoi' : 'Next run'} :{' '}
                    {formatNext(status?.jobs?.find((j) => j.id === 'cart_abandonment_campaign')?.next_run_time)}
                    <span className="mx-1">·</span>
                    {language === 'fr' ? 'Toutes les 6h' : 'Every 6h'} · {language === 'fr' ? 'panier 24-72h' : 'cart 24-72h'}
                  </p>
                </div>
              </div>
              <Button
                onClick={() => runCampaign('cart-abandonment')}
                disabled={runningJob === 'cart-abandonment'}
                size="sm"
                data-testid="run-cart-campaign-btn"
              >
                {runningJob === 'cart-abandonment' ? (
                  <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> {language === 'fr' ? 'En cours…' : 'Running…'}</>
                ) : (
                  <><PlayCircle className="w-4 h-4 mr-1" /> {language === 'fr' ? 'Lancer maintenant' : 'Run now'}</>
                )}
              </Button>
            </div>

            {/* Post-delivery review */}
            <div className="flex items-center justify-between p-4 border border-border rounded-lg" data-testid="campaign-row-review">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center">
                  <Star className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="font-semibold flex items-center gap-2">
                    {language === 'fr' ? 'Retour client (avis + fidélité)' : 'Post-delivery review'}
                    <Badge variant="outline" className="text-xs">-10% · 30j</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                    <Clock className="w-3 h-3" />
                    {language === 'fr' ? 'Prochain envoi' : 'Next run'} :{' '}
                    {formatNext(status?.jobs?.find((j) => j.id === 'post_delivery_review_campaign')?.next_run_time)}
                    <span className="mx-1">·</span>
                    {language === 'fr' ? '3-7 jours après livraison' : '3-7 days after delivery'}
                    <span className="mx-1">·</span>
                    <Package className="w-3 h-3" /> {s.delivered_orders_eligible ?? 0} {language === 'fr' ? 'éligibles' : 'eligible'}
                  </p>
                </div>
              </div>
              <Button
                onClick={() => runCampaign('post-delivery-review')}
                disabled={runningJob === 'post-delivery-review'}
                size="sm"
                data-testid="run-review-campaign-btn"
              >
                {runningJob === 'post-delivery-review' ? (
                  <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> {language === 'fr' ? 'En cours…' : 'Running…'}</>
                ) : (
                  <><PlayCircle className="w-4 h-4 mr-1" /> {language === 'fr' ? 'Lancer maintenant' : 'Run now'}</>
                )}
              </Button>
            </div>

            {/* Dormant reactivation */}
            <div className="flex items-center justify-between p-4 border border-border rounded-lg" data-testid="campaign-row-reactivation">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-400 to-emerald-600 flex items-center justify-center">
                  <Heart className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="font-semibold flex items-center gap-2">
                    {language === 'fr' ? 'Réactivation clients dormants' : 'Dormant reactivation'}
                    <Badge variant="outline" className="text-xs">-15% · 14j</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                    <Clock className="w-3 h-3" />
                    {language === 'fr' ? 'Prochain envoi' : 'Next run'} :{' '}
                    {formatNext(status?.jobs?.find((j) => j.id === 'reactivation_campaign')?.next_run_time)}
                    <span className="mx-1">·</span>
                    {language === 'fr' ? 'Chaque lundi 11h UTC' : 'Every Monday 11h UTC'}
                    <span className="mx-1">·</span>
                    <UserX className="w-3 h-3" /> {s.dormant_customers_count ?? 0} {language === 'fr' ? 'dormants' : 'dormant'}
                  </p>
                </div>
              </div>
              <Button
                onClick={() => runCampaign('reactivation')}
                disabled={runningJob === 'reactivation'}
                size="sm"
                data-testid="run-reactivation-campaign-btn"
              >
                {runningJob === 'reactivation' ? (
                  <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> {language === 'fr' ? 'En cours…' : 'Running…'}</>
                ) : (
                  <><PlayCircle className="w-4 h-4 mr-1" /> {language === 'fr' ? 'Lancer maintenant' : 'Run now'}</>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Email performance (Resend webhooks) */}
      <Card data-testid="email-performance-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[#0F4C5C]" />
            {language === 'fr' ? 'Performance des emails' : 'Email performance'}
          </CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            {language === 'fr'
              ? 'Taux d\'ouverture et de clic par campagne, alimentés par les webhooks Resend.'
              : 'Open and click rates per campaign, powered by Resend webhooks.'}
          </p>
        </CardHeader>
        <CardContent>
          {(!emailStats || emailStats.total_events === 0) && (
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-900" data-testid="resend-webhook-setup-notice">
              <p className="font-semibold mb-1">
                {language === 'fr' ? '📡 Configuration requise (une seule fois)' : '📡 One-time setup required'}
              </p>
              <ol className="list-decimal list-inside space-y-1 text-xs">
                <li>{language === 'fr' ? 'Allez sur resend.com → Webhooks → Add Webhook' : 'Go to resend.com → Webhooks → Add Webhook'}</li>
                <li className="flex items-center gap-1 flex-wrap">
                  {language === 'fr' ? 'URL :' : 'URL:'}
                  <code className="bg-white px-1.5 py-0.5 rounded border text-[11px]">{emailStats?.webhook_url || '—'}</code>
                  <button
                    type="button"
                    onClick={() => navigator.clipboard.writeText(emailStats?.webhook_url || '').then(() => toast.success('URL copiée'), () => toast.info(emailStats?.webhook_url || '', { duration: 10000 }))}
                    className="inline-flex items-center text-blue-700 underline"
                    data-testid="copy-webhook-url-btn"
                  >
                    <Copy className="w-3 h-3 mr-0.5" />{language === 'fr' ? 'copier' : 'copy'}
                  </button>
                </li>
                <li>{language === 'fr' ? 'Cochez les événements : sent, delivered, opened, clicked, bounced' : 'Select events: sent, delivered, opened, clicked, bounced'}</li>
                <li>{language === 'fr' ? 'Copiez le "Signing Secret" (whsec_…) dans RESEND_WEBHOOK_SECRET du .env backend' : 'Copy the "Signing Secret" (whsec_…) into RESEND_WEBHOOK_SECRET in the backend .env'}</li>
              </ol>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="email-stats-table">
              <thead>
                <tr className="text-left text-xs text-muted-foreground border-b">
                  <th className="py-2 pr-4">{language === 'fr' ? 'Campagne' : 'Campaign'}</th>
                  <th className="py-2 pr-4 text-right">{language === 'fr' ? 'Envoyés' : 'Sent'}</th>
                  <th className="py-2 pr-4 text-right">{language === 'fr' ? 'Délivrés' : 'Delivered'}</th>
                  <th className="py-2 pr-4 text-right">{language === 'fr' ? 'Ouverts' : 'Opened'}</th>
                  <th className="py-2 pr-4 text-right">{language === 'fr' ? 'Taux ouverture' : 'Open rate'}</th>
                  <th className="py-2 pr-4 text-right">{language === 'fr' ? 'Clics' : 'Clicks'}</th>
                  <th className="py-2 pr-4 text-right">{language === 'fr' ? 'Taux clic' : 'Click rate'}</th>
                  <th className="py-2 pr-4 text-right">{language === 'fr' ? 'Commandes' : 'Orders'}</th>
                  <th className="py-2 text-right">{language === 'fr' ? 'CA généré' : 'Revenue'}</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { key: 'birthday', label: language === 'fr' ? '🎂 Anniversaires' : '🎂 Birthdays' },
                  { key: 'cart_abandonment', label: language === 'fr' ? '🛒 Panier abandonné' : '🛒 Abandoned cart' },
                  { key: 'post_delivery_review', label: language === 'fr' ? '⭐ Retour client' : '⭐ Post-delivery review' },
                  { key: 'reactivation', label: language === 'fr' ? '💚 Réactivation' : '💚 Reactivation' },
                ].map(({ key, label }) => {
                  const c = emailStats?.campaigns?.[key] || {};
                  return (
                    <tr key={key} className="border-b last:border-0" data-testid={`email-stats-row-${key}`}>
                      <td className="py-2.5 pr-4 font-medium">{label}</td>
                      <td className="py-2.5 pr-4 text-right">{c.sent ?? 0}</td>
                      <td className="py-2.5 pr-4 text-right">{c.delivered ?? 0}</td>
                      <td className="py-2.5 pr-4 text-right">{c.opened ?? 0}</td>
                      <td className="py-2.5 pr-4 text-right font-semibold text-[#0F4C5C]">{c.open_rate ?? 0}%</td>
                      <td className="py-2.5 pr-4 text-right">{c.clicked ?? 0}</td>
                      <td className="py-2.5 pr-4 text-right font-semibold text-[#0F4C5C]">{c.click_rate ?? 0}%</td>
                      <td className="py-2.5 pr-4 text-right">{c.orders_count ?? 0}</td>
                      <td className="py-2.5 text-right font-bold text-emerald-700" data-testid={`campaign-revenue-${key}`}>
                        €{(c.revenue ?? 0).toFixed(2)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
